import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { LedgerValueDisplay } from './LedgerValueDisplay';
import { LedgerStatusIcon, ledgerNeedsUpdate } from './LedgerStatusIcon';
import type { Ledger, LedgerType } from '@/api/types';
import { isLedgerArchived } from '@/utils/ledger';

interface LedgerRowProps {
    ledger: Ledger;
    type: LedgerType;
    onConfirmUpdate: (ledgerId: number) => void;
    onRevertUpdate: (ledgerId: number) => void;
    isUpdating: boolean;
    userLatestConfirmationDate?: number | null;
}

/**
 * Individual ledger row with responsive mobile/desktop layouts.
 * Mobile: Card-style with expandable details for assets.
 * Desktop: Table row with all columns visible.
 */
export function LedgerRow({
    ledger,
    type,
    onConfirmUpdate,
    onRevertUpdate,
    isUpdating,
    userLatestConfirmationDate,
}: LedgerRowProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();

    const isArchived = isLedgerArchived(ledger);
    const needsUpdate = ledgerNeedsUpdate(ledger.confirmedUpdatedDate, userLatestConfirmationDate);
    // Assets show returns columns (MWRR, P&L, P&L%), debts do not
    const showReturnsColumns = type === 'asset';

    // Archived rows have red background; non-archived are transparent (parent handles background + hover)
    const rowClasses = isArchived
        ? 'bg-red-50 dark:bg-red-900/20'
        : '';

    /**
     * Format gain percentage with sign and color.
     */
    const formatGainPercentage = (value: number | null | undefined) => {
        if (value == null) {
            return <span className="text-gray-500 dark:text-gray-400">-</span>;
        }
        const colorClass = value >= 0
            ? 'text-green-700 dark:text-green-300'
            : 'text-red-700 dark:text-red-300';
        const sign = value >= 0 ? '+' : '';
        return <span className={colorClass}>{sign}{value.toFixed(2)}%</span>;
    };

    /**
     * Format return rate (MWRR) with color.
     */
    const formatReturnRate = (value: number | null | undefined) => {
        if (value == null) {
            return <span className="text-gray-500 dark:text-gray-400">-</span>;
        }
        const colorClass = value >= 0
            ? 'text-green-700 dark:text-green-300'
            : 'text-red-700 dark:text-red-300';
        return <span className={colorClass}>{value.toFixed(2)}%</span>;
    };

    return (
        <div className={rowClasses}>
            {/* Mobile View */}
            <div className="lg:hidden">
                <div
                    className="flex items-center justify-between p-4 min-h-[52px] cursor-pointer"
                    onClick={() => navigate(`/ledgers/${ledger.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/ledgers/${ledger.id}`);
                        }
                    }}
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        {showReturnsColumns && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(!isExpanded);
                                }}
                                className="shrink-0 p-1 -m-1"
                                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                            >
                                <ChevronDownIcon
                                    className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${
                                        isExpanded ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                            {ledger.label}
                        </span>
                    </div>
                    <div
                        className="flex items-center space-x-3 shrink-0 ml-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <LedgerValueDisplay
                            latestValue={ledger.latestValue}
                            size="md"
                            className="text-gray-900 dark:text-white"
                        />
                        <LedgerStatusIcon
                            isArchived={isArchived}
                            needsUpdate={needsUpdate}
                            onConfirmUpdate={() => onConfirmUpdate(ledger.id)}
                            onRevertUpdate={() => onRevertUpdate(ledger.id)}
                            isLoading={isUpdating}
                        />
                    </div>
                </div>

                {/* Expanded Content for Mobile (Only for assets with returns columns) */}
                {showReturnsColumns && isExpanded && (
                    <div className="bg-gray-50 dark:bg-dark border-t border-gray-300 dark:border-gray-600 px-4 py-3">
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">
                                    MWRR
                                </p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {formatReturnRate(ledger.analytics?.mwrr)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">
                                    P&L
                                </p>
                                <MoneyDisplay
                                    value={ledger.analytics?.profitLoss ?? null}
                                    size="sm"
                                    colorize
                                />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">
                                    P&L(%)
                                </p>
                                <p className="text-sm">
                                    {formatGainPercentage(ledger.analytics?.gainPercentage)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop View */}
            <Link
                to={`/ledgers/${ledger.id}`}
                className="hidden lg:flex items-center min-h-[52px]"
            >
                {/* Label */}
                <div className="flex-1 px-4 py-3 min-w-0">
                    <span className="text-gray-900 dark:text-white hover:text-accent hover:underline truncate block">
                        {ledger.label}
                    </span>
                </div>

                {/* MWRR - only for assets */}
                {showReturnsColumns && (
                    <div className="w-24 px-4 py-3 text-right shrink-0">
                        {formatReturnRate(ledger.analytics?.mwrr)}
                    </div>
                )}

                {/* P&L - only for assets */}
                {showReturnsColumns && (
                    <div className="w-28 px-4 py-3 text-right shrink-0">
                        <MoneyDisplay
                            value={ledger.analytics?.profitLoss ?? null}
                            size="sm"
                            colorize
                        />
                    </div>
                )}

                {/* P&L % - only for assets */}
                {showReturnsColumns && (
                    <div className="w-28 px-4 py-3 text-right shrink-0">
                        {formatGainPercentage(ledger.analytics?.gainPercentage)}
                    </div>
                )}

                {/* Value / Balance */}
                <div className="w-48 px-4 py-3 text-right shrink-0">
                    <div className="flex items-center justify-end space-x-3">
                        <LedgerValueDisplay
                            latestValue={ledger.latestValue}
                            size="md"
                            className="text-gray-900 dark:text-white font-medium"
                        />
                        <LedgerStatusIcon
                            isArchived={isArchived}
                            needsUpdate={needsUpdate}
                            onConfirmUpdate={() => onConfirmUpdate(ledger.id)}
                            onRevertUpdate={() => onRevertUpdate(ledger.id)}
                            isLoading={isUpdating}
                        />
                    </div>
                </div>
            </Link>
        </div>
    );
}
